import React, {createContext, useContext, useMemo, useState, useEffect, useLayoutEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import styled from 'styled-components';
import {useSelector} from 'react-redux';
import {isEmpty} from 'lodash';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import {useSettleState} from './useSettleState';

const NAV_PANEL_ID = 'fit-navigation-panel';
// Scope the top-bar lift to the Kronos top nav only. The shell may render
// other MotifNav-wrapped chrome (e.g. Hercules web) that should NOT be
// touched by Kronos's FirstLoadOverlay.
const TOP_BAR_SELECTOR = '.StyledMotifNavWrapper.kronos-topbar-nav';
const MFE_SELECTOR = '.AresMFEContainer';
// Host of the app-level toast. It owns its own stacking context, so its children (including the
// bootstrap progress messages raised while this overlay is up) cannot escape the overlay on their
// own -- the host itself has to be lifted.
const TOAST_HOST_SELECTOR = '.StyledGuidedWorkflowBanners';
// Flowchart modal that hosts the minimal-view step. While this modal is open
// the outer shell's FirstLoadOverlay must NOT lift fit-navigation-panel /
// top bar above its z-index, otherwise the parent shell's nav panel pokes
// through the modal chrome.
const FLOWCHART_MODAL_SELECTOR = '.superkronos-modal';

// ---------------------------------------------------------------------------
// Context -- consumed by children via useFirstLoadOverlay()
// ---------------------------------------------------------------------------

const FirstLoadOverlayContext = createContext({
	isLoading: true,
	setContentLoading: () => {},
	hasProvider: false
});

export const useFirstLoadOverlay = () => useContext(FirstLoadOverlayContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Determines the global "loading" state for the Kronos content area.
 *
 * Loading is true when:
 *   - APIs are not stably settled (see useSettleState), OR
 *   - A child has signalled loading via setContentLoading(true)
 *
 * On route changes the settle state resets automatically.
 * On unhandled errors contentLoading is force-cleared so the overlay
 * never permanently hides an error page.
 */
export const FirstLoadOverlayProvider = ({children}) => {
	const {pathname} = useLocation();
	const isApiStablySettled = useSettleState();
	const hasBlockingError = useSelector(({errors}) => !isEmpty(errors) && !errors?.isObjectNotFoundError && (errors?.level ?? 'error') === 'error');

	const [contentLoading, setContentLoading] = useState(false);

	const prevPathnameRef = useRef(pathname);
	useEffect(() => {
		if (pathname !== prevPathnameRef.current) {
			prevPathnameRef.current = pathname;
			setContentLoading(false);
		}
	}, [pathname]);

	useEffect(() => {
		if (hasBlockingError) {
			setContentLoading(false);
		}
	}, [hasBlockingError]);

	useEffect(() => {
		const clearOnError = () => setContentLoading(false);
		window.addEventListener('error', clearOnError);
		window.addEventListener('unhandledrejection', clearOnError);
		return () => {
			window.removeEventListener('error', clearOnError);
			window.removeEventListener('unhandledrejection', clearOnError);
		};
	}, []);

	const isLoading = !hasBlockingError && (!isApiStablySettled || contentLoading);
	const contextValue = useMemo(() => ({isLoading, setContentLoading, hasProvider: true}), [isLoading, setContentLoading]);

	return <FirstLoadOverlayContext.Provider value={contextValue}>{children}</FirstLoadOverlayContext.Provider>;
};

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

const StyledOverlay = styled.div`
	position: ${({$mfe}) => ($mfe ? 'absolute' : 'fixed')};
	top: ${({$top}) => $top}px;
	left: ${({$left}) => $left}px;
	right: ${({$right}) => $right}px;
	bottom: ${({$bottom}) => $bottom}px;
	z-index: var(--z-topmost-fullscreen) !important;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: var(--neutrals-bg-gradient1, #fff);
`;

/**
 * Bootstrap (no Provider): fullscreen loader matching master behaviour.
 *
 * Inside Provider (section changes): scoped overlay covering only the
 * content area — right of #fit-navigation-panel, below .StyledMotifNavWrapper.
 * 100 % opacity so no content is visible behind it.
 * Works in both MFE and standalone mode.
 */
/**
 * Apply an inline style change to el and record an "undo" entry into `applied`
 * so the original value can be restored once the overlay is hidden. Idempotent
 * per (element, prop) pair: the original value is captured only on first apply.
 */
const applyStyle = (applied, el, prop, value) => {
	if (!el) return;
	const alreadyApplied = applied.some((entry) => entry.el === el && entry.prop === prop);
	if (!alreadyApplied) {
		applied.push({el, prop, original: el.style[prop]});
	}
	el.style[prop] = value;
};

/**
 * Lifts a chrome element (nav panel / top bar) above the overlay's z-index
 * so it remains visible while the overlay is shown.
 */
const liftAboveOverlay = (applied, el) => {
	if (!el) return;
	if (!el.style.position || el.style.position === 'static') {
		applyStyle(applied, el, 'position', 'relative');
	}
	applyStyle(applied, el, 'zIndex', '1001');
};

const restoreStyles = (applied) => {
	applied.forEach(({el, prop, original}) => {
		el.style[prop] = original;
	});
	applied.length = 0;
};

/**
 * Compute overlay bounds synchronously so the very first paint is correct.
 * Returns {isMfe, top, left, right, bottom} or null for fullscreen bootstrap.
 *
 * In MFE mode the overlay covers the entire .AresMFEContainer (inset:0) and
 * nav panel / top bar are lifted via z-index so they punch through visually.
 * Pixel-based offset math against a deeply nested layout was unreliable.
 *
 * All inline-style mutations are recorded into `applied` so they can be
 * reverted as soon as the overlay disappears (otherwise the lifted z-index
 * traps dropdowns/drawers/toasts that expect to render above the chrome).
 */
const computeOverlayBounds = (hasProvider, applied, ownContainer) => {
	// MFE mode is a property of THIS overlay instance, not of the document as a whole. Derive it from
	// whether this instance actually lives inside its own .AresMFEContainer (ownContainer), never from
	// the global presence of any container. Multiple Kronos instances (the background document plus a
	// CanvasCoreServices SlideOutDrawer hosting the kronos-drawer MFE) share the DOM: the background
	// app renders its overlay OUTSIDE any container, so a document-wide check would flip the background
	// overlay into MFE mode the moment a drawer mounts its container, wrongly suppressing/rescoping the
	// background's own loading state. Keying off ownContainer keeps every chrome mutation scoped to the
	// instance that owns it, and lets the container-less background fall through to document-scoped
	// (standalone) behavior.
	const isMfeMode = !!ownContainer;

	const scope = isMfeMode ? ownContainer : document;
	const navPanel = scope ? scope.querySelector(`#${NAV_PANEL_ID}`) : null;
	const topBar = scope ? scope.querySelector(TOP_BAR_SELECTOR) : null;
	const mfeContainer = isMfeMode ? ownContainer : null;
	// When the flowchart modal (which hosts minimal view) is open, anything the
	// modal renders sits on top of the shell. Lifting the shell's chrome would
	// push it above the modal and produce the visual shift / leaked nav panel.
	const flowchartModalOpen = !!document.querySelector(FLOWCHART_MODAL_SELECTOR);

	if (!mfeContainer && !hasProvider) return null; // standalone bootstrap → fullscreen loader

	if (mfeContainer) applyStyle(applied, mfeContainer, 'position', 'relative');

	if (!flowchartModalOpen) {
		liftAboveOverlay(applied, navPanel);
		liftAboveOverlay(applied, topBar);
	}

	if (mfeContainer) return {isMfe: true, top: 0, left: 0, right: 0, bottom: 0};

	return {
		isMfe: false,
		top: topBar ? topBar.getBoundingClientRect().bottom : 0,
		left: navPanel ? navPanel.getBoundingClientRect().right : 0,
		right: 0,
		bottom: 0
	};
};

const FirstLoadOverlay = () => {
	const {isLoading, hasProvider} = useFirstLoadOverlay();
	const appliedStylesRef = useRef([]);
	// Persistent hidden anchor so we can resolve THIS instance's own .AresMFEContainer.
	const anchorRef = useRef(null);
	// undefined = not computed yet, null = fullscreen bootstrap loader,
	// object = scoped overlay bounds.
	const [bounds, setBounds] = useState(undefined);

	// All DOM reads/mutations run in a layout effect (after the anchor ref is attached, before
	// paint) so a nested instance (the document drawer) never touches another instance's chrome,
	// and there is no first-render flash.
	useLayoutEffect(() => {
		if (!isLoading) {
			restoreStyles(appliedStylesRef.current);
			setBounds(undefined);
			return undefined;
		}
		const ownContainer = anchorRef.current ? anchorRef.current.closest(MFE_SELECTOR) : null;
		const next = computeOverlayBounds(hasProvider, appliedStylesRef.current, ownContainer);
		setBounds(next);
		// Lift the toast host only within this instance's own scope so the background toast host
		// is never touched by the drawer. Mode is per-instance (ownContainer), not document-wide.
		const isMfeMode = !!ownContainer;
		const toastScope = isMfeMode ? ownContainer : document;
		if (toastScope && !document.querySelector(FLOWCHART_MODAL_SELECTOR)) {
			liftAboveOverlay(appliedStylesRef.current, toastScope.querySelector(TOAST_HOST_SELECTOR));
		}
		return () => restoreStyles(appliedStylesRef.current);
	}, [isLoading, hasProvider]);

	useEffect(() => {
		return () => restoreStyles(appliedStylesRef.current);
	}, []);

	// Always render the anchor so getOwnContainer resolution works before/after the overlay shows.
	const anchor = <span ref={anchorRef} style={{display: 'none'}} aria-hidden="true" />;

	// Not loading or not computed yet: render only the hidden anchor.
	if (!isLoading || bounds === undefined) {
		return anchor;
	}

	if (!bounds) {
		return (
			<>
				{anchor}
				<LoadingIndicator show fullscreen styleName="FirstLoadOverlay" />
			</>
		);
	}

	return (
		<>
			{anchor}
			<StyledOverlay
				className="FirstLoadOverlayWrapper"
				$mfe={bounds.isMfe}
				$top={bounds.top}
				$left={bounds.left}
				$right={bounds.right}
				$bottom={bounds.bottom}
			>
				<LoadingIndicator show styleName="FirstLoadOverlay" />
			</StyledOverlay>
		</>
	);
};

export default FirstLoadOverlay;
